import React, { useState } from "react";
import { X, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";
import "./ChangePasswordFirstTimeModal.css";

const ChangePasswordFirstTimeModal = ({
  visible,
  onClose,
  onSubmit,
  isLoading = false,
  userEmail = "",
}) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  if (!visible) return null;

  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChar,
      isValid:
        minLength &&
        hasUpperCase &&
        hasLowerCase &&
        hasNumber &&
        hasSpecialChar,
    };
  };

  const passwordStrength = validatePassword(newPassword);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!currentPassword.trim()) {
      newErrors.currentPassword = "Current password is required";
    }

    if (!newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (!passwordStrength.isValid) {
      newErrors.newPassword = "Password does not meet all requirements";
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (currentPassword === newPassword && currentPassword.trim()) {
      newErrors.newPassword =
        "New password must be different from current password";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onSubmit({
        currentPassword,
        newPassword,
        confirmPassword,
      });
    }
  };

  const handleReset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrors({});
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <div className="change-password-modal-overlay" onClick={handleClose}>
      <div
        className="change-password-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="change-password-close-button"
          onClick={handleClose}
          disabled={isLoading}
        >
          <X size={24} />
        </button>

        <div className="change-password-header">
          <div className="change-password-icon-container">
            <Lock size={40} />
          </div>
          <h2 className="change-password-title">Change Your Password</h2>
          <p className="change-password-subtitle">
            For your security, please update your default password
          </p>
          {userEmail && (
            <div className="change-password-email-badge">{userEmail}</div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="change-password-form">
          <div className="change-password-input-group">
            <label className="change-password-label">Current Password</label>
            <div className="change-password-input-wrapper">
              <Lock size={20} className="change-password-input-icon" />
              <input
                type={showCurrentPassword ? "text" : "password"}
                className={`change-password-input ${
                  errors.currentPassword ? "change-password-input-error" : ""
                }`}
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  if (errors.currentPassword) {
                    setErrors({ ...errors, currentPassword: null });
                  }
                }}
                disabled={isLoading}
              />
              <button
                type="button"
                className="change-password-toggle-button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                disabled={isLoading}
              >
                {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.currentPassword && (
              <span className="change-password-error-text">
                <AlertCircle size={14} />
                {errors.currentPassword}
              </span>
            )}
          </div>

          <div className="change-password-input-group">
            <label className="change-password-label">New Password</label>
            <div className="change-password-input-wrapper">
              <Lock size={20} className="change-password-input-icon" />
              <input
                type={showNewPassword ? "text" : "password"}
                className={`change-password-input ${
                  errors.newPassword ? "change-password-input-error" : ""
                }`}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (errors.newPassword) {
                    setErrors({ ...errors, newPassword: null });
                  }
                }}
                disabled={isLoading}
              />
              <button
                type="button"
                className="change-password-toggle-button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                disabled={isLoading}
              >
                {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.newPassword && (
              <span className="change-password-error-text">
                <AlertCircle size={14} />
                {errors.newPassword}
              </span>
            )}
          </div>

          {newPassword && (
            <div className="change-password-requirements">
              <p className="change-password-requirements-title">
                Password must contain:
              </p>
              <div className="change-password-requirements-list">
                <div
                  className={`change-password-requirement ${
                    passwordStrength.minLength ? "valid" : ""
                  }`}
                >
                  {passwordStrength.minLength ? (
                    <CheckCircle size={16} />
                  ) : (
                    <div className="change-password-requirement-dot"></div>
                  )}
                  <span>At least 8 characters</span>
                </div>
                <div
                  className={`change-password-requirement ${
                    passwordStrength.hasUpperCase ? "valid" : ""
                  }`}
                >
                  {passwordStrength.hasUpperCase ? (
                    <CheckCircle size={16} />
                  ) : (
                    <div className="change-password-requirement-dot"></div>
                  )}
                  <span>One uppercase letter</span>
                </div>
                <div
                  className={`change-password-requirement ${
                    passwordStrength.hasLowerCase ? "valid" : ""
                  }`}
                >
                  {passwordStrength.hasLowerCase ? (
                    <CheckCircle size={16} />
                  ) : (
                    <div className="change-password-requirement-dot"></div>
                  )}
                  <span>One lowercase letter</span>
                </div>
                <div
                  className={`change-password-requirement ${
                    passwordStrength.hasNumber ? "valid" : ""
                  }`}
                >
                  {passwordStrength.hasNumber ? (
                    <CheckCircle size={16} />
                  ) : (
                    <div className="change-password-requirement-dot"></div>
                  )}
                  <span>One number</span>
                </div>
                <div
                  className={`change-password-requirement ${
                    passwordStrength.hasSpecialChar ? "valid" : ""
                  }`}
                >
                  {passwordStrength.hasSpecialChar ? (
                    <CheckCircle size={16} />
                  ) : (
                    <div className="change-password-requirement-dot"></div>
                  )}
                  <span>One special character</span>
                </div>
              </div>
            </div>
          )}

          <div className="change-password-input-group">
            <label className="change-password-label">
              Confirm New Password
            </label>
            <div className="change-password-input-wrapper">
              <Lock size={20} className="change-password-input-icon" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                className={`change-password-input ${
                  errors.confirmPassword ? "change-password-input-error" : ""
                }`}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) {
                    setErrors({ ...errors, confirmPassword: null });
                  }
                }}
                disabled={isLoading}
              />
              <button
                type="button"
                className="change-password-toggle-button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="change-password-error-text">
                <AlertCircle size={14} />
                {errors.confirmPassword}
              </span>
            )}
          </div>

          <div className="change-password-note">
            <AlertCircle size={18} />
            <p>
              Make sure your password is strong and unique. Don't reuse
              passwords from other accounts.
            </p>
          </div>

          <div className="change-password-actions">
            <button
              type="button"
              className="change-password-button change-password-button-cancel"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="change-password-button change-password-button-submit"
              disabled={isLoading || !passwordStrength.isValid}
            >
              {isLoading ? (
                <span className="change-password-spinner"></span>
              ) : (
                "Update Password"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordFirstTimeModal;
